// Time in the UI is the operator's own clock.
//
// The API is UTC and stays UTC — that is the only sane wire format when checks
// are run from workers on three continents. But an operator reading a report at
// 09:00 in Berlin should not have to subtract two hours in their head to decide
// whether an outage is still happening. So every timestamp is converted on the
// way to the screen, here, once: a page that formats its own dates is a page
// that will eventually disagree with the one next to it.
//
// Two shapes arrive from the API:
//   * RFC3339 with a zone — `generatedAt`, `createdAt`, `checkedAt`, … Parsing
//     these is unambiguous.
//   * `report.date` — a string the generator pre-formats as "2006-01-02 15:04"
//     in UTC. It carries no zone, and `new Date("2026-03-27 14:30")` reads a
//     zone-less string as LOCAL time, which silently shifts it by the viewer's
//     offset. That one is normalised to UTC before parsing.

const ZONELESS = /^(\d{4}-\d{2}-\d{2})[ T](\d{2}:\d{2}(:\d{2})?)$/;

/** parseApiDate turns any timestamp the API emits into a Date, or null. */
export function parseApiDate(value: string | number | Date | null | undefined): Date | null {
  if (value === null || value === undefined || value === '') return null;
  if (value instanceof Date) return isNaN(value.getTime()) ? null : value;
  if (typeof value === 'number') {
    // Unix seconds (what Redis-backed records carry) vs. milliseconds.
    const d = new Date(value < 1e11 ? value * 1000 : value);
    return isNaN(d.getTime()) ? null : d;
  }
  const m = ZONELESS.exec(value.trim());
  const d = new Date(m ? `${m[1]}T${m[2]}Z` : value);
  if (isNaN(d.getTime())) return null;
  // Go's zero time marshals as year 1 — "never happened", not a real moment.
  return d.getFullYear() > 1970 ? d : null;
}

/** formatDateTime renders a timestamp as date + time in the viewer's timezone. */
export function formatDateTime(
  value: string | number | Date | null | undefined,
  fallback = '--',
): string {
  const d = parseApiDate(value);
  return d ? d.toLocaleString() : fallback;
}

/** formatDateTimeCompact is formatDateTime without the year and seconds — for
 * places that stack several timestamps in a table cell, where the full form
 * costs more width than the year is worth. */
export function formatDateTimeCompact(
  value: string | number | Date | null | undefined,
  fallback = '--',
): string {
  const d = parseApiDate(value);
  if (!d) return fallback;
  return d.toLocaleString(undefined, {
    day: '2-digit', month: '2-digit', hour: '2-digit', minute: '2-digit',
  });
}

/** formatTime renders only the clock part, in the viewer's timezone. */
export function formatTime(
  value: string | number | Date | null | undefined,
  fallback = '--',
): string {
  const d = parseApiDate(value);
  return d ? d.toLocaleTimeString() : fallback;
}

/** localTimeZone is the viewer's IANA zone, for labelling a screen full of times. */
export function localTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'local time';
  } catch {
    return 'local time';
  }
}
